import React, { Fragment, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Carousel from "react-bootstrap/Carousel";
import Form from "react-bootstrap/Form";
import DamageType from "components/DamageType/DamageType";
import Select from "react-select";
import GridPaging from "components/Paging/GridPaging";
import SortDirectionIcon from "components/SortDirectionIcon";
import ToolButton from "components/ToolButton/ToolButton";
import "./PestInformation.css";

import {
  getDamageTypes,
  getPestSummaryByPest,
  getAllstates,
  getAllYears,
} from "../../services/pestTableService";
import { getPestsByStateAndHost } from "../../services/mapService";

const ForestPestByPest = () => {
  const [selectedPest, setSelectedPest] = useState();
  const [selectedState, setSelectedState] = useState();
  const [selectedType, setSelectedType] = useState();
  const [selectedYear, setSelectedYear] = useState();
  const [pestList, setPestList] = useState();
  const [stateList, setStateList] = useState();
  const [typeList, setTypeList] = useState();
  const [yearList, setYearList] = useState(null);
  const [tableData, setTableData] = useState();
  const [initialLoad, setInitialLoad] = useState(true);
  const [currentPageData, setCurrentPageData] = useState();
  const [yearColumn, setYearColumn] = useState();
  const [sortAscending, setSortAscending] = useState(true);
  const [sortedField, setSortedField] = useState("CommonName");
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [pestRowVisible, setPestRowVisible] = useState(null);
  const [includeAbiotic, setIncludeAbiotic] = useState(false);
  const announcement = useRef(null);
  const totalPages = useRef(1);
  const totalRecords = useRef(0);
  const years = useRef();
  const sortedData = useRef();
  const selectState = useRef(null);
  const selectPest = useRef(null);
  const mapPestOptions = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const esriPublisherID = "gGHDlz6USftL5Pau";
  const esriLayerName = "FPC_Centroids_2019_2023";
  let defaultYear = 2023;

  // When filters change
  useEffect(() => {
    if (!initialLoad) {
      setLoading(true);
      setCurrentPage(1);
      setPestRowVisible(null);
      getTableData();
    }
  }, [
    initialLoad,
    selectedPest,
    selectedState,
    selectedType,
    selectedYear,
    includeAbiotic,
  ]);

  useEffect(() => {
    if (!initialLoad) {
      setYearColumn(selectedYear.label);
    }
  }, [selectedYear]);

  useEffect(() => {
    if (!initialLoad) {
      setLoading(true);
      setPestRowVisible(null);
      setCurrentPage(1);
    }
  }, [sortedField, sortAscending]);

  useEffect(() => {
    if (tableData) {
      sortData(sortedField, sortAscending);
    }
  }, [tableData]);

  useEffect(() => {
    if (currentPageData) {
      setLoading(false);
    }
  }, [currentPageData]);

  useEffect(() => {
    setLoading(true);
    sliceDataIntoPages();
  }, [pageSize, currentPage]);

  // Initial load
  useEffect(() => {
    const paramPest = searchParams.get("p");
    const paramState = searchParams.get("st");
    const paramDamageType = searchParams.get("dt");
    const paramYear = searchParams.get("yr");
    const paramAbiotic = searchParams.get("ab");
    let foundPest = null;
    let foundState = null;
    let foundDamageType = null;
    let foundYear = null;
    announcement.current = "You are now on the By Pest table page";

    // States
    getAllstates().then((result) => {
      const stateOptions = result.data;
      const allStates = {
        value: 0,
        label: "All States",
        isDisabled: false,
      };
      stateOptions.unshift(allStates);
      const listOfStates = Array.from(stateOptions);
      setStateList(listOfStates);
      // Apply URL parameter if there is one different from the default
      if (paramState && paramState !== "All States") {
        foundState = listOfStates.find(
          (element) => element.label === paramState
        );
        if (foundState) {
          setSelectedState(foundState);
        }
      }
      // Damage Types
      getDamageTypes().then((result) => {
        const typesOptions = result.data;
        const listOfDamageTypes = Array.from(typesOptions);
        setTypeList(listOfDamageTypes);
        // Apply URL parameter if there is one different from the default
        if (
          paramDamageType &&
          paramDamageType !== "All Damage Types" &&
          paramDamageType !== "All Types"
        ) {
          foundDamageType = listOfDamageTypes.find(
            (element) => element.label === paramDamageType
          );
          if (foundDamageType) {
            setSelectedType(foundDamageType);
          }
        }
      });
      // Abiotic agents
      if (paramAbiotic) {
        setIncludeAbiotic(true);
      }
      // Years
      getAllYears().then((result) => {
        years.current = result.data.map((item) => item.value).slice(0, 5);
        const yearOptionList = years.current.map((yr) => ({
          key: yr,
          value: yr,
          label: yr.toString(),
        }));
        setYearList(yearOptionList);
        // Apply URL parameter if there is one different from the default
        if (paramYear && paramYear !== yearOptionList[0].value) {
          foundYear = yearOptionList.find(
            (element) => element.label === paramYear
          );
          if (foundYear) {
            setSelectedYear(foundYear);
            setYearColumn(foundYear.label);
          } else {
            foundYear = null;
            setSelectedYear(yearOptionList[0]);
            setYearColumn(yearOptionList[0].label);
          }
        } else {
          setSelectedYear(yearOptionList[0]);
          setYearColumn(yearOptionList[0].label);
        }
        // Pests
        // Get list of pests with map data
        getPestsByStateAndHost(
          esriPublisherID,
          esriLayerName,
          "All States",
          "All Trees"
        ).then((result) => {
          mapPestOptions.current = result.data.features;
        });
        let filter = {
          damageTypeId: 93,
          years: years.current,
        };
        getPestSummaryByPest(filter).then((result) => {
          const rawPestData = result.data.data;
          const pestList = [];
          rawPestData.forEach((element) => {
            if (!pestList.includes(element.CommonName)) {
              pestList.push(element.CommonName);
            }
          });
          pestList.sort((a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1));
          const pestOptions = pestList.map((item) => ({
            value: item,
            label: item,
          }));
          const allPests = {
            value: 0,
            label: "All Forest Pests",
            isDisabled: false,
          };
          pestOptions.unshift(allPests);
          setPestList(pestOptions);
          // Apply URL parameter if there is one different from the default
          if (paramPest && paramPest !== "All Forest Pests") {
            foundPest = pestOptions.find(
              (element) => element.label === paramPest
            );
            if (foundPest) {
              setSelectedPest(foundPest);
            }
            setInitialLoad(false);
          } else {
            setInitialLoad(false);
          }
        });
      });
    });
    document.title = "Forest Pest Conditions - By Pest";
  }, []);

  const showPestImages = (rowID) => {
    setCarouselIndex(0);
    const pestImageSection = document.getElementById(rowID + "-images");
    pestImageSection.classList.remove("hidden");
    setPestRowVisible(rowID + "-images");
  };

  const collapsePestImages = (rowID) => {
    const pestImageSection = document.getElementById(rowID);
    pestImageSection.classList.add("hidden");
    setPestRowVisible(null);
  };

  const getTableData = () => {
    let filter = {
      years: [defaultYear],
      damageTypeId: 93,
      StateFips: 0,
    };
    if (selectedYear) {
      filter.years = [selectedYear.value];
    }
    if (selectedType) {
      filter.damageTypeId = selectedType.value;
    }
    if (selectedState) {
      filter.StateFips = selectedState.value;
    }
    getPestSummaryByPest(filter).then((result) => {
      const allData = result.data.data;
      // Generate a map link if data are available
      if (mapPestOptions.current) {
        allData.forEach((element) => {
          const validPest = mapPestOptions.current.find(
            (el) => el.attributes.DCA_COMMON === element.CommonName
          );
          if (validPest) {
            const mapLink =
              "/map/?state=All%20States&tree=All%20Trees&pest=" +
              encodeURIComponent(element.CommonName);
            element.mapLink = mapLink;
          }
        });
      }
      // Filter results if a pest is selected
      if (selectedPest && selectedPest.label !== "All Forest Pests") {
        const filteredData = allData.filter(
          (element) => element.CommonName === selectedPest.label
        );
        totalRecords.current = filteredData.length;
        // Filter results if abiotic agents are excluded
        if (includeAbiotic) {
          setTableData(filteredData);
        } else {
          const filteredData2 = filteredData.filter(
            (element) => element.Category !== "Abiotic Agents"
          );
          totalRecords.current = filteredData2.length;
          setTableData(filteredData2);
        }
      } else {
        if (includeAbiotic) {
          totalRecords.current = allData.length;
          setTableData(allData);
        } else {
          const filteredData2 = allData.filter(
            (element) => element.Category !== "Abiotic Agents"
          );
          totalRecords.current = filteredData2.length;
          setTableData(filteredData2);
        }
      }
    });
  };

  const formatNumber = (input) => {
    if (input > 0 && input < 1) {
      return 1;
    } else {
      return Math.round(input).toLocaleString();
    }
  };

  const toggleAbiotic = () => {
    if (!initialLoad) {
      setIncludeAbiotic((prevState) => !prevState);
      announcement.current =
        "The table has been filtered to " +
        (includeAbiotic ? "exclude" : "include") +
        " abiotic agents.";
    }
  };

  const toggleSort = (field) => {
    if (!initialLoad) {
      let asc = !sortAscending;
      setCurrentPage(1);
      if (sortedField === field) {
        const previousSort = sortAscending;
        setSortAscending((prevState) => !prevState);
        sortData(field, !previousSort);
      } else {
        asc = true;
        setSortedField(field);
        setSortAscending(true);
        sortData(field, true);
      }
      announcement.current =
        "The table has been sorted by column " +
        (field === "CommonName" ? "Forest Pest" : field) +
        " in " +
        (asc ? "ascending" : "descending") +
        " order";
    }
  };

  const sortData = (field, asc) => {
    if (tableData) {
      sortedData.current = field
        ? [...tableData].sort((a, b) => {
            const aValue = isNaN(parseFloat(a[field]))
              ? a[field].toLowerCase()
              : parseFloat(a[field]);
            const bValue = isNaN(parseFloat(b[field]))
              ? b[field].toLowerCase()
              : parseFloat(b[field]);
            if (asc) {
              return aValue < bValue ? -1 : 1;
            } else {
              return aValue > bValue ? -1 : 1;
            }
          })
        : tableData;
      sliceDataIntoPages();
    }
  };

  const sliceDataIntoPages = () => {
    if (sortedData.current) {
      totalPages.current = Math.ceil(sortedData.current.length / pageSize);
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = Math.min(
        startIndex + pageSize - 1,
        sortedData.current.length - 1
      );
      if (sortedData.current.length === 0) {
        announcement.current = "No records match your filter selections";
      }
      setCurrentPageData(sortedData.current.slice(startIndex, endIndex + 1));
    }
  };

  const capitalize = (str) => {
    return str[0].toUpperCase() + str.slice(1, str.length);
  };

  // Function to handle page size
  const handleChangePageSize = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  // Function to handle page change
  const handlePageChange = (page) => {
    setPestRowVisible(null);
    setCurrentPage(page);
  };

  function handlePrint() {
    const lblState = selectedState != null ? selectedState.label : "All States";
    const lblDamageType =
      selectedType != null ? selectedType.label : "All Damage Types";
    const table = document.getElementById(
      "hidden-table-for-copy-and-print"
    ).outerHTML;
    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write("<html><head><title>By Pest</title>");
    printWindow.document.write("<style>");
    printWindow.document.write(
      "table {border-collapse: collapse; width: 100%;}  td, th { border:0.5px solid black; text-align:left; } th{background-color: #f0f0f0;} tr:nth-child(odd) { background-color: #edf1e6;} "
    );

    printWindow.document.write("</style>");
    printWindow.document.write("</head><body>");
    printWindow.document.write(
      "<h2>By Pest</h2>" +
        "<h3>" +
        lblState +
        " - " +
        lblDamageType +
        " (acres) </h3>"
    );
    printWindow.document.write(table);
    printWindow.document.write("</body></html>");
    printWindow.document.close();
    printWindow.print();
  }
  // Copy to clipboard
  function handleCopy() {
    const tableHTML = document.getElementById(
      "hidden-table-for-copy-and-print"
    ).outerHTML;
    navigator.permissions.query({ name: "clipboard-write" }).then((result) => {
      if (result.state === "granted") {
        navigator.clipboard.writeText(tableHTML);
      }
    });
  }
  // Share the link
  function handleShare() {
    let url = window.location.href;
    if (url.indexOf("?") > -1) {
      url = url.substring(0, url.indexOf("?"));
    }
    const shareDt =
      selectedType &&
      selectedType.label !== "All Damage Types" &&
      selectedType.label !== "All Types"
        ? selectedType.label
        : null;
    const shareSt =
      selectedState && selectedState.label !== "All States"
        ? selectedState.label
        : null;
    const shareP =
      selectedPest && selectedPest.label !== "All Forest Pests"
        ? selectedPest.label
        : null;
    const shareYr =
      selectedYear && selectedYear.value !== defaultYear
        ? selectedYear.label
        : null;
    const shareAb = includeAbiotic ? "true" : null;
    let urlComponents = [];
    if (shareDt || shareSt || shareP || shareYr || shareAb) {
      url += "?";
    }
    if (shareP) {
      urlComponents.push("p=" + encodeURIComponent(shareP));
    }
    if (shareSt) {
      urlComponents.push("st=" + encodeURIComponent(shareSt));
    }
    if (shareDt) {
      urlComponents.push("dt=" + encodeURIComponent(shareDt));
    }
    if (shareYr) {
      urlComponents.push("yr=" + encodeURIComponent(shareYr));
    }
    if (shareAb) {
      urlComponents.push("ab=true");
    }
    url += urlComponents.join("&");
    navigator.permissions.query({ name: "clipboard-write" }).then((result) => {
      if (result.state === "granted") {
        navigator.clipboard.writeText(url);
      }
    });
  }

  return (
    <main className="grid-container pest-information" id="main-content">
      <h1 className="main-heading">By Pest</h1>
      <p className="pest-information-intro">
        The default view includes all damage types and biotic agents only,
        combined as damage displayed in acres. Abiotic agents such as drought
        and frost are excluded. Data are derived from the authoritative Forest
        Health Protection{" "}
        <a
          href="https://www.fs.usda.gov/science-technology/data-tools-and-products/fhp-mapping-reporting/detection-surveys"
          target="_blank"
          rel="noreferrer"
          className="usa-link"
        >
          Insect and Disease Survey
        </a>{" "}
        database. To filter the data, select from the options below.{" "}
      </p>
      <DamageType />
      <br />
      <br />
      <div className="grid-row">
        <div className="tablet:grid-col-4 mobile-lg:grid-col-12">
          <span className="label_text">Select Forest Pest</span>
          <Select
            ref={selectPest}
            className="react-select top-row"
            isDisabled={loading}
            placeholder="All Forest Pests"
            aria-label="Select a forest pest. Press the down arrow to expand the option list. Press delete
                  to clear the current selection."
            name="singleSelect"
            value={selectedPest}
            onChange={(value) => setSelectedPest(value)}
            options={pestList}
            isClearable={true}
            styles={{
              placeholder: (baseStyles) => ({
                ...baseStyles,
                color: "#1b1b1b",
              }),
            }}
          />
        </div>
        <div className="tablet:grid-col-3 mobile-lg:grid-col-12">
          <span className="label_text">Select Damage Type</span>
          <Select
            className="react-select top-row"
            isDisabled={loading}
            placeholder="All Types"
            aria-label="Select a damage type. Press the down arrow to expand the option list. Press delete
            to clear the current selection."
            name="singleSelect"
            value={selectedType}
            onChange={(value) => setSelectedType(value)}
            options={typeList}
            isClearable={true}
            styles={{
              placeholder: (baseStyles) => ({
                ...baseStyles,
                color: "#1b1b1b",
              }),
            }}
          />
        </div>
        <div className="tablet:grid-col-3 mobile-lg:grid-col-12">
          <span className="label_text">Select State</span>
          <Select
            ref={selectState}
            className="react-select"
            isDisabled={loading}
            placeholder="All States"
            aria-label="Select a state. Press the down arrow to expand the option list. Press delete
                  to clear the current selection."
            name="singleSelect"
            value={selectedState}
            onChange={(value) => setSelectedState(value)}
            options={stateList}
            isClearable={true}
            styles={{
              placeholder: (baseStyles) => ({
                ...baseStyles,
                color: "#1b1b1b",
              }),
            }}
          />
        </div>
        <div className="tablet:grid-col-2 mobile-lg:grid-col-12">
          <span className="label_text">Select Year</span>
          <Select
            className="react-select end-input"
            isDisabled={loading}
            placeholder="2022"
            aria-label="Select a year. Press the down arrow to expand the option list. Press delete
            to clear the current selection."
            name="singleSelect"
            value={selectedYear}
            onChange={(value) => setSelectedYear(value)}
            options={yearList}
            isClearable={false}
            styles={{
              placeholder: (baseStyles) => ({
                ...baseStyles,
                color: "#1b1b1b",
              }),
            }}
          />
        </div>
      </div>
      <br />
      <div className="grid-row">
        <div className="tablet:grid-col-3 mobile-lg:grid-col-12">
          <span className="show-records_label">Show </span>
          <select
            className="paging_select"
            value={pageSize}
            onChange={handleChangePageSize}
            name="singleSelect"
            placeholder="All"
            aria-label="All"
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value={tableData?.length}>All</option>
          </select>
          <span className="show-records_label"> Records</span>
        </div>
        <div
          id="abiotic-checkbox-container"
          className="tablet:grid-col-6 mobile-lg:grid-col-12"
        >
          <Form.Check
            type="checkbox"
            checked={includeAbiotic}
            label="Include Abiotic Agents"
            id="abiotic-checkbox"
            onClick={() => toggleAbiotic()}
            onKeyDown={(e) => {
              if (e.key === "Enter") toggleAbiotic();
            }}
            readOnly
          />
        </div>
        <div className="tablet:grid-col-3 mobile-lg:grid-col-12">
          <ToolButton
            handlePrint={handlePrint}
            handleCopy={handleCopy}
            handleShare={handleShare}
          ></ToolButton>
        </div>
      </div>
      <div className="visually-hidden" aria-live="polite" aria-busy={loading}>
        {announcement.current}
      </div>
      <div className="grid-col-12 usa-table-container--scrollable">
        {loading ? (
          <div className="spinner-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <div>
            {currentPageData?.length > 0 ? (
              <table
                aria-label="Pest Information by Pest"
                className="usa-table"
              >
                <thead>
                  <tr>
                    <th
                      tabIndex="0"
                      onClick={() => toggleSort("CommonName")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") toggleSort("CommonName");
                      }}
                      scope="col"
                      className="pest-column"
                      aria-label="Forest Pest (sortable)"
                    >
                      FOREST PEST
                      <SortDirectionIcon
                        asc={sortAscending}
                        visible={sortedField === "CommonName"}
                      />
                    </th>
                    <th
                      tabIndex="0"
                      onClick={() => toggleSort("Category")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") toggleSort("Category");
                      }}
                      scope="col"
                      className="category-column"
                      aria-label="Category (sortable)"
                    >
                      CATEGORY
                      <SortDirectionIcon
                        asc={sortAscending}
                        visible={sortedField === "Category"}
                      />
                    </th>
                    <th
                      tabIndex="0"
                      onClick={() => toggleSort("Region")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") toggleSort("Region");
                      }}
                      scope="col"
                      className="region-column"
                      aria-label="Region (sortable)"
                    >
                      REGION
                      <SortDirectionIcon
                        asc={sortAscending}
                        visible={sortedField === "Region"}
                      />
                    </th>
                    <th
                      tabIndex="0"
                      onClick={() => toggleSort("State")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") toggleSort("State");
                      }}
                      scope="col"
                      className="state-column"
                      aria-label="State (sortable)"
                    >
                      STATE
                      <SortDirectionIcon
                        asc={sortAscending}
                        visible={sortedField === "State"}
                      />
                    </th>
                    {selectedYear && (
                      <th
                        tabIndex="0"
                        onClick={() => toggleSort(selectedYear.label)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") toggleSort(selectedYear.label);
                        }}
                        scope="col"
                        className="year-column"
                        aria-label={selectedYear.label + " acres (sortable)"}
                      >
                        {selectedYear.label + " (acres)"}
                        <SortDirectionIcon
                          asc={sortAscending}
                          visible={sortedField === selectedYear.label}
                        />
                      </th>
                    )}
                    <th scope="col" className="action-column">
                      ACTIONS
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentPageData.map((item, index) => (
                    <Fragment
                      key={
                        item["CommonName"] +
                        "-" +
                        item["Region"] +
                        "-" +
                        item["State"]
                      }
                    >
                      <tr
                        key={
                          item["CommonName"] +
                          "-" +
                          item["Region"] +
                          "-" +
                          item["State"]
                        }
                        id={
                          item["CommonName"].replaceAll(" ", "-") +
                          "-" +
                          item["State"].replaceAll(" ", "-") +
                          "-" +
                          index
                        }
                      >
                        <td>{item["CommonName"]}</td>
                        <td>{item["Category"]}</td>
                        <td>{item["Region"]}</td>
                        <td>{item["State"]}</td>
                        <td>{formatNumber(item[yearColumn])}</td>
                        <td>
                          {item["picFile1"] && (
                            <button
                              className="pest-table-btn photo"
                              aria-label={
                                "View " + item["CommonName"] + " images"
                              }
                              title={"View " + item["CommonName"] + " images"}
                              onClick={() =>
                                showPestImages("pest-table-" + index)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter")
                                  showPestImages("pest-table-" + index);
                              }}
                            >
                              Photo
                            </button>
                          )}
                          {item["WebLink"] && (
                            <a
                              href={item["WebLink"]}
                              className="pest-table-btn fidl"
                              tabIndex="0"
                              title={
                                "Open Forest Insect & Disease Leaflet (FIDL) for " +
                                item["CommonName"]
                              }
                              target="new"
                            >
                              FIDL
                            </a>
                          )}
                          {item["mapLink"] && (
                            <a
                              href={item["mapLink"]}
                              className="pest-table-btn"
                              title={
                                "View " +
                                item["CommonName"] +
                                " in Interactive Map"
                              }
                              tabIndex="0"
                              target="new"
                            >
                              Map
                            </a>
                          )}
                        </td>
                      </tr>
                      {item["picFile1"] && (
                        <Fragment>
                          <tr
                            key={
                              item["CommonName"] +
                              "-" +
                              item["Region"] +
                              "-" +
                              item["State"] +
                              "-images"
                            }
                            id={"pest-table-" + index + "-images"}
                            className={
                              "photo-section-tr" +
                              (pestRowVisible !==
                              "pest-table-" + index + "-images"
                                ? " hidden"
                                : "")
                            }
                          >
                            <td colSpan="6" className="photo-section-div">
                              <i
                                tabIndex="0"
                                className="fa fa-close pest-table-carousel-close-btn"
                                title="Close pest image viewer"
                                aria-label="Close pest image viewer"
                                onClick={() => {
                                  collapsePestImages(
                                    "pest-table-" + index + "-images"
                                  );
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter")
                                    collapsePestImages(
                                      "pest-table-" + index + "-images"
                                    );
                                }}
                              />
                              {item["picFile1"] ? (
                                <div className="pest-table-carousel-container">
                                  <Carousel
                                    defaultActiveIndex={0}
                                    onSelect={(selectedIndex) =>
                                      setCarouselIndex(selectedIndex)
                                    }
                                    data-bs-theme="dark"
                                    controls={true}
                                    interval={null}
                                  >
                                    <Carousel.Item key={item["picFile1"]}>
                                      <img
                                        src={"/images/" + item["picFile1"]}
                                        alt={item["picDesc1"]}
                                        className="pest-table-carousel__image"
                                      />
                                      <Carousel.Caption>
                                        <div className="pest-table-carousel__pestname">
                                          {capitalize(item["CommonName"])}
                                        </div>
                                        <div className="pest-table-carousel__title">
                                          {item["picDesc1"]}
                                        </div>
                                        <div className="pest-table-carousel__subtitle">
                                          {item["picCred1"]}
                                        </div>
                                      </Carousel.Caption>
                                    </Carousel.Item>
                                    {item["picFile2"] && (
                                      <Carousel.Item key={item["picFile2"]}>
                                        <img
                                          src={"/images/" + item["picFile2"]}
                                          alt={item["picDesc2"]}
                                          className="pest-table-carousel__image"
                                        />
                                        <Carousel.Caption>
                                          <div className="pest-table-carousel__pestname">
                                            {capitalize(item["CommonName"])}
                                          </div>
                                          <div className="pest-table-carousel__title">
                                            {item["picDesc2"]}
                                          </div>{" "}
                                          <div className="pest-table-carousel__subtitle">
                                            {item["picCred2"]}
                                          </div>
                                        </Carousel.Caption>
                                      </Carousel.Item>
                                    )}
                                    {item["picFile3"] && (
                                      <Carousel.Item key={item["picFile3"]}>
                                        <img
                                          src={"/images/" + item["picFile3"]}
                                          alt={item["picDesc3"]}
                                          className="pest-table-carousel__image"
                                        />
                                        <Carousel.Caption>
                                          <div className="pest-table-carousel__pestname">
                                            {capitalize(item["CommonName"])}
                                          </div>
                                          <div className="pest-table-carousel__title">
                                            {item["picDesc3"]}
                                          </div>{" "}
                                          <div className="pest-table-carousel__subtitle">
                                            {item["picCred3"]}
                                          </div>
                                        </Carousel.Caption>
                                      </Carousel.Item>
                                    )}
                                  </Carousel>
                                </div>
                              ) : (
                                "No photos available"
                              )}
                            </td>
                          </tr>
                          <tr
                            className="hidden"
                            key={
                              item["CommonName"] +
                              "-" +
                              item["Region"] +
                              "-" +
                              item["State"] +
                              "-hidden"
                            }
                          >
                            <td>No content</td>
                          </tr>
                        </Fragment>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="label_text">
                No records match your filter selections
              </p>
            )}
          </div>
        )}
      </div>
      {!loading && currentPageData && currentPageData.length > 0 && (
        <GridPaging
          currentPage={currentPage}
          totalPages={totalPages.current}
          handlePageChange={handlePageChange}
          pageSize={pageSize}
          totalRecords={totalRecords.current}
        />
      )}
      {sortedData.current && (
        <div
          id="hidden-table-for-copy-and-print"
          className="grid-col-12 usa-table-container--scrollable"
          aria-hidden="true"
        >
          <table>
            <thead>
              <tr>
                <th style={{ padding: "4px" }}>FOREST PEST</th>
                <th style={{ padding: "4px" }}>CATEGORY</th>
                <th style={{ padding: "4px" }}>REGION</th>
                <th style={{ padding: "4px" }}>STATE</th>
                {selectedYear && (
                  <th style={{ padding: "4px" }}>
                    {selectedYear.label}
                    {" (acres)"}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {sortedData.current.map((item, index) => (
                <tr
                  key={
                    item["CommonName"] +
                    "-" +
                    item["Region"] +
                    "-" +
                    item["State"]
                  }
                >
                  <td style={{ padding: "4px" }}>{item["CommonName"]}</td>
                  <td style={{ padding: "4px" }}>{item["Category"]}</td>
                  <td style={{ padding: "4px" }}>{item["Region"]}</td>
                  <td style={{ padding: "4px" }}>{item["State"]}</td>
                  <td style={{ padding: "4px" }}>
                    {formatNumber(item[yearColumn])}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
};

export default ForestPestByPest;
